import { Controller } from '@hotwired/stimulus';
import { CableConsumer } from 'lib/cable_consumer';
import csrfToken from 'stimulus/csrf_token';

const EXTRACTION_TIMEOUT_MS = 90000;
const GENERIC_ERROR = 'An unexpected error occurred. Please try again.';
const TIMEOUT_NOTICE = "This is taking longer than expected. We'll open your job post as soon as " +
  'it is ready, and you will also find it in your drafts.';

// Both tabs POST to the existing extraction endpoints, which answer 202 and hand the work
// to a worker; the outcome arrives over JobAdExtractionChannel.
export default class extends Controller {
  static targets = ['submit', 'text'];

  static outlets = ['modal-component'];

  static values = { textUrl: String, fileUrl: String, minLength: Number };

  connect() {
    this.tab = 'text';
    this.running = false;
    this.selectedFile = null;

    // On connect, not on submit: ActionCable does not buffer a broadcast sent before the
    // subscription lands, and the worker can finish very fast.
    this.subscription = CableConsumer.subscriptions.create(
      { channel: 'JobAdExtractionChannel' },
      { received: (data) => this.received(data) }
    );

    this.refresh();
  }

  disconnect() {
    this.subscription?.unsubscribe();
    clearTimeout(this.watchdog);
  }

  tabChanged(event) {
    this.tab = event.detail.value;
    this.refresh();
  }

  fileSelected(event) {
    this.selectedFile = event.detail.file;
    this.refresh();
  }

  refresh() {
    if (!this.hasSubmitTarget) return;

    this.submitTarget.disabled = this.running || !this.ready();
  }

  async extract() {
    if (this.running || !this.ready()) return;

    this.start();

    try {
      const response = this.tab === 'file' ? await this.postFile() : await this.postText();
      if (response.status === 202) {
        // The worker can fail and broadcast before this response lands, and stop() has run.
        if (this.running) this.watchdog = setTimeout(() => this.timedOut(), EXTRACTION_TIMEOUT_MS);
        return;
      }

      const body = await response.json().catch(() => ({}));
      this.stop(body.error || GENERIC_ERROR);
    } catch {
      this.stop(GENERIC_ERROR);
    }
  }

  received(data) {
    if (!this.running) return;

    if (data.status === 'complete' && data.redirect_url) {
      this.running = false;
      clearTimeout(this.watchdog);
      window.location.href = data.redirect_url;
      return;
    }

    this.stop(data.error || GENERIC_ERROR);
  }

  // The worker still finishes and persists the draft, so keep listening and keep the button
  // disabled: a retry here would pay for a second extraction and leave a duplicate draft.
  timedOut() {
    if (this.hasModalComponentOutlet) this.modalComponentOutlet.forceClose();
    this.alert(TIMEOUT_NOTICE, 'notice');
  }

  postText() {
    return fetch(this.textUrlValue, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
      body: JSON.stringify({ job_text: this.text() }),
    });
  }

  postFile() {
    const body = new FormData();
    body.append('file', this.file());

    return fetch(this.fileUrlValue, {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken() },
      body,
    });
  }

  ready() {
    return this.tab === 'file' ? Boolean(this.file()) : this.text().length >= this.minLengthValue;
  }

  text() {
    return this.hasTextTarget ? this.textTarget.value.trim() : '';
  }

  // Taken from the uploader's event, not the input: the input briefly holds a file the
  // uploader is about to reject.
  file() {
    return this.selectedFile;
  }

  start() {
    this.running = true;
    this.refresh();
    if (this.hasModalComponentOutlet) this.modalComponentOutlet.open();
  }

  stop(message) {
    this.running = false;
    clearTimeout(this.watchdog);
    this.refresh();
    // forceClose, not close: close() is the user-dismissal path, which a non-dismissible
    // modal is meant to ignore.
    if (this.hasModalComponentOutlet) this.modalComponentOutlet.forceClose();

    this.alert(message);
  }

  alert(message, level = 'alert') {
    if (message && window.Alerts) window.Alerts[level](message);
  }
}
